import React, { useCallback, useMemo } from 'react';
import { useHierarchyArea } from '@plarium/hierarchy-react';

import { Grid } from '@gogame/components';
import {
  withForm,
  getIn,
  SidePageV2,
  FormField,
  FormFieldGroup,
} from '@gogame/form';
import { useChildComponent } from '@gogame/hooks-ts';
import { dateFNS } from '@gogame/intl-ts';

import { loadMediaSource } from 'scenes/Campaigns/sources/campaigns';
import { CreateMediaSourceForm } from 'scenes/Campaigns/GGCampaigns/components/CreateMediaSourceForm';
import {
  MultiCampaigns,
  MultiCampaignsCostModels,
} from 'scenes/Campaigns/components/Form/Campaign';
import { CAMPAIGN_PLATFORM_ID } from 'scenes/Campaigns/components/Form/Campaign/constants';
import {
  isEmptyCampaign,
  createPlatformCampaign,
} from 'scenes/Campaigns/components/Form/Campaign/schema';
import {
  useLinksConfig,
  DEEP_LINK_PLATFORM_TOKENS,
  DEEP_LINK_PLATFORM,
  platformTokenSchema,
} from 'scenes/Campaigns/components/Form/DeepLink';
import { FraudProtectionArray } from 'scenes/Campaigns/components/Form/FraudProtection';
import {
  NETWORK,
  PLATFORM,
  LOCALE,
  CONVERSION_PAGES,
  CONVERSION_PAGE_FLOW,
  CAMPAIGNS,
  LINK_FORMAT_TYPE,
  REGISTRATIONS_FRAUD,
  INSTALLS_FRAUD,
  AFFILIATE,
  LINK,
  TEST_CAMPAIGN_END_DATE,
  TYPE,
  CAMPAIGN_TYPE_RE_ENGAGEMENT,
  REWARD_PACKAGE,
  LINKS_CONFIG,
  TRACKING_TYPE,
  DEEP_TYPE,
  HYBRYD_TYPE,
  CTV_TYPE,
  AGENCY_TYPE,
  APPLICATION,
  MISSING_MEDIA_SOURCE,
  PARENT_CAMPAIGN,
  SPEND_ALLOCATION_TYPE,
} from 'scenes/Campaigns/components/Form/constants';
import { useFraudPresetValues } from 'scenes/Campaigns/components/Form/hooks/useFraudPresetValues';
import { useGetPlatformPermissions } from 'scenes/Campaigns/components/Form/hooks/useGetPlatformPermissions';
import {
  LabelsField,
  PromoCodeField,
  LinkFormatField,
  OwnerField,
  AffiliateField,
  ApplicationField,
  MultiChangeNetworkField,
  MultiChangePlatformField,
  LocaleField,
  ConversionPageFlowsField,
  ConversionPagesField,
  CountryField,
  TypeField,
  TrackREField,
  MediaSourceField,
  ButtonsField,
  CpDomainField,
  CampaignPartnerInfoField,
  ParentCampaignNameField,
} from 'scenes/Campaigns/components/Form/fields';
import { CPPSection } from 'scenes/Campaigns/components/Form/sections/CPPSection';
import { CampaignsLinksSection } from 'scenes/Campaigns/components/Form/sections/CampaignsLinksSection';
import { ConversionPageSection } from 'scenes/Campaigns/components/Form/sections/ConversionPageSection';
import { SpendAllocationModelSection } from 'scenes/Campaigns/components/Form/sections/SpendAllocationModelSection';
import { CostModelsSection } from 'scenes/Campaigns/components/Form/sections/CostModelsSection';
import { GeneralSection } from 'scenes/Campaigns/components/Form/sections/GeneralSection';
import { LinkFormatSection } from 'scenes/Campaigns/components/Form/sections/LinkFormatSection';
import { LinkFields } from 'scenes/Campaigns/components/Form/sections/LinksSection';
import { PartnerCampaignIdSection } from 'scenes/Campaigns/components/Form/sections/PartnerCampaignIdSection';
import { PromoCodeSection } from 'scenes/Campaigns/components/Form/sections/PromoCodeSection';
import { RewardsSection } from 'scenes/Campaigns/components/Form/sections/RewardsSection';
import { TargetingSection } from 'scenes/Campaigns/components/Form/sections/TargetingSection';
import WarningStatus from 'scenes/Campaigns/components/Form/sections/WarningStatus';
import { DEPENDENCIES } from 'scenes/Campaigns/components/Form/dependencies';

import { linksActivateSchema, multiCampaignsSchema } from './schema';

export function normalizeMultiPlatformsDeepLink(value, adjustTokens) {
  if (!adjustTokens.length) {
    return undefined;
  }

  const nextPlatformTokens = adjustTokens.map(({ platformId }) => {
    return platformTokenSchema.cast({
      [DEEP_LINK_PLATFORM]: platformId,
    });
  });
  return {
    ...value,
    [DEEP_LINK_PLATFORM_TOKENS]: nextPlatformTokens,
  };
}

export function normalizeLinks(value, links) {
  if (!links.length) {
    return {
      links: [],
    };
  }

  return {
    ...value,
    links,
  };
}

export function normalizeMediaSource(_, data) {
  return data.missingMediaSource;
}

export function resetCampaigns({ platforms, campaigns }) {
  let nextCampaigns = [];
  let matchedByPlatformId = false;

  if (!platforms.length) {
    return [createPlatformCampaign({ name: campaigns?.[0]?.name })];
  }

  // at first try to keep campaign by platform id
  platforms.forEach((platform) => {
    const prevCampaign = campaigns.find(
      (campaign) => campaign[CAMPAIGN_PLATFORM_ID] === platform.id
    );
    if (prevCampaign) {
      matchedByPlatformId = true;
      nextCampaigns.push(prevCampaign);
    } else {
      nextCampaigns.push({
        [CAMPAIGN_PLATFORM_ID]: platform.id,
        link: {},
      });
    }
  });

  if (!matchedByPlatformId) {
    // if no platform match, keep campaign by index
    const notEmptyPrevCampaigns = campaigns.filter(
      (platformCampaign) => !isEmptyCampaign(platformCampaign)
    );
    nextCampaigns = nextCampaigns.map((campaign, i) => {
      const prevCampaign = notEmptyPrevCampaigns[i];
      return createPlatformCampaign({
        ...prevCampaign,
        ...campaign,
      });
    });
  }

  return nextCampaigns;
}

const MultiCampaignsForm = withForm({
  enableReinitialize: true,
  validationSchema: () => multiCampaignsSchema,
  normalizeEffect: ({ normalizedValues, changes }) => {
    if (changes.hasOwnProperty(PLATFORM)) {
      const { data, normalizedValue } = changes[PLATFORM];
      const option = data.find((opt) => opt.id === normalizedValue);
      if (!option) {
        return {
          ...normalizedValues,
          [CAMPAIGNS]: resetCampaigns({
            platforms: [{}],
            campaigns: normalizedValues[CAMPAIGNS],
          }),
        };
      }
    }
    return normalizedValues;
  },
})(({
  values,
  onClose,
  loading,
  setFieldValue,
  setValues,
  hierarchyState,
  title,
  name,
  CostModelFormCreate,
  CostModelFormEdit,
  removableCostModels,
  onExited,
  open,
  isStatsDateAvailable,
}) => {
  console.log('values', values);
  const {
    [PLATFORM]: platformId,
    [NETWORK]: networkId,
    [APPLICATION]: appId,
    [AFFILIATE]: affiliateId,
    [CAMPAIGNS]: campaigns,
    [REWARD_PACKAGE]: rewardPackage,
    linkType,
  } = values;

  const [childComponent, openChildComponent] = useChildComponent();

  const { data } = useHierarchyArea({
    name: MISSING_MEDIA_SOURCE,
    source: loadMediaSource,
    active: !!(networkId && affiliateId),
    depends: [NETWORK, AFFILIATE],
    normalizeValue: normalizeMediaSource,
  });

  const linksActive = linksActivateSchema.isValidSync(values);

  //use targeting params for config
  const { linksConfig } = useLinksConfig({
    name: LINKS_CONFIG,
    depends: DEPENDENCIES[LINKS_CONFIG],
    active: linksActive,
    normalizeValue: normalizeLinks,
  });

  const fieldsConfig =
    linksConfig?.find((i) => i.type.id === linkType?.id)?.fieldsConfiguration ||
    [];

  const visibleFields = fieldsConfig.reduce((p, n) => {
    return { ...p, [n.fieldName]: n.enabled };
  }, {});

  const mediaSource = data && values.missingMediaSource;

  const { conversionPagesAvailable, linkFormatAvailable } =
    useGetPlatformPermissions(platformId, hierarchyState);

  const withRegistrationsFraud = !!values[REGISTRATIONS_FRAUD];
  const withInstallsFraud = !!values[INSTALLS_FRAUD];

  const { platforms } = useMemo(() => {
    const data = getIn(hierarchyState, `${PLATFORM}.data`);

    const selectedPlatform = data && data.find(({ id }) => id === platformId);
    return selectedPlatform ? selectedPlatform : { platforms: [] };
  }, [hierarchyState, platformId]);

  const handleChangeNetwork = useCallback(
    (e, v, opt = {}) => {
      const {
        conversionPagesAvailable,
        linkFormatAvailable,
        isGgLinkAvailable,
      } = opt;
      const name = e?.target?.name;

      setValues({
        ...values,
        [name]: v,
        [CAMPAIGNS]: resetCampaigns({
          platforms: [{}],
          campaigns: [
            createPlatformCampaign({ name: values?.campaigns?.[0]?.name }),
          ],
        }),
        [PLATFORM]: undefined,
        [LINK_FORMAT_TYPE]: linkFormatAvailable
          ? values[LINK_FORMAT_TYPE]
          : undefined,
        [LOCALE]: conversionPagesAvailable ? values[LOCALE] : undefined,
        [CONVERSION_PAGES]: conversionPagesAvailable
          ? values[CONVERSION_PAGES]
          : undefined,
        [CONVERSION_PAGE_FLOW]: conversionPagesAvailable
          ? values[CONVERSION_PAGE_FLOW]
          : undefined,
        [LINK_FORMAT_TYPE]: conversionPagesAvailable
          ? values[LINK_FORMAT_TYPE]
          : undefined,
        [LINK]: isGgLinkAvailable ? values[isGgLinkAvailable] : null,
      });
    },
    [setValues, values]
  );

  const handleChangePlatform = useCallback(
    (e, v, opt) => {
      const name = e?.target?.name;
      const nextPlatforms = opt?.platforms || [];
      const prevCampaigns = values[CAMPAIGNS];

      setValues({
        ...values,

        [name]: v,
        [CAMPAIGNS]: resetCampaigns({
          platforms: nextPlatforms,
          campaigns: prevCampaigns,
        }),
        ...(nextPlatforms?.length > 1
          ? {
              [PARENT_CAMPAIGN]: {
                [SPEND_ALLOCATION_TYPE]: 0,
              },
            }
          : null),
      });
    },
    [setValues, values]
  );

  useFraudPresetValues({
    withRegistrationsFraud,
    withInstallsFraud,
    values,
    setFieldValue,
  });

  const handleValidateActivityType = (trackRE) => {
    if (!trackRE && values[TYPE] === CAMPAIGN_TYPE_RE_ENGAGEMENT) {
      return 'Re-Engagement Campaign should Track RE';
    }
  };

  const handleAddMediaSource = () => {
    return new Promise((resolve) => {
      openChildComponent(CreateMediaSourceForm, {
        onSubmit: async (mediaSource) => {
          resolve(mediaSource);
        },
      });
    });
  };

  const isRewardVisible =
    visibleFields.Reward ||
    visibleFields.AdditionalReward ||
    visibleFields.AdditionalRewardCondition;

  const isCPPVisible = visibleFields.AndroidCPP || visibleFields.IosCPP;

  const isCPVisible =
    visibleFields.AndroidConversionPage || visibleFields.IosConversionPage;

  const handleTypeChange = (_e, value) => {
    const cleanCampaigns = (campaigns) => {
      return campaigns.map((campaign) => {
        const {
          link,
          agencyLink,

          conversionPageId,
          customProductPageParams,
          ...rest
        } = campaign;

        return {
          ...rest,
          ...(value === TRACKING_TYPE && { link }),
          ...(value === AGENCY_TYPE && { agencyLink }),
          ...((visibleFields.AndroidConversionPage ||
            visibleFields.IosConversionPage) && { conversionPageId }),
          ...((visibleFields.AndroidCPP || visibleFields.IosCPP) && {
            customProductPageParams,
          }),
        };
      });
    };

    const {
      hybridLink,
      ctvLink,
      deeplink,
      rewardPackageId,
      additionalRewardPackageId,
      additionalRewardConditionId,
      campaigns,
      ...rest
    } = values;

    setValues({
      ...rest,
      linkType: linksConfig?.find((i) => i?.type?.id === value)?.type,

      linkTypeId: value,

      ...(visibleFields.Reward && { rewardPackageId }),
      ...(visibleFields.AdditionalReward && { additionalRewardPackageId }),
      ...(visibleFields.AdditionalRewardCondition && {
        additionalRewardConditionId,
      }),

      campaigns: cleanCampaigns(campaigns),
      ...(value === DEEP_TYPE && { deeplink }),
      ...(value === HYBRYD_TYPE && { hybridLink }),
      ...(value === CTV_TYPE && { ctvLink }),
    });
  };

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={name} />
      <SidePageV2.NewBody>
        <TargetingSection
          affiliate={<AffiliateField />}
          application={<ApplicationField />}
          network={
            <MultiChangeNetworkField onMultiChange={handleChangeNetwork} />
          }
          platform={
            <MultiChangePlatformField onMultiChange={handleChangePlatform} />
          }
          domain={conversionPagesAvailable ? <CpDomainField /> : null}
          locale={conversionPagesAvailable ? <LocaleField /> : null}
          conversionPageFlows={
            conversionPagesAvailable ? <ConversionPageFlowsField /> : null
          }
          conversionPages={
            conversionPagesAvailable ? (
              <ConversionPagesField
                application={appId}
                platform={platformId}
                affiliate={affiliateId}
              />
            ) : null
          }
          country={<CountryField />}
          mediaSource={
            mediaSource ? (
              <MediaSourceField onAdd={handleAddMediaSource} />
            ) : null
          }
          activityType={
            <TrackREField
              onValidate={handleValidateActivityType}
              active={appId !== undefined && affiliateId !== undefined}
            />
          }
        />

        <GeneralSection
          parentCampaign={
            platforms?.length > 1 ? (
              <FormFieldGroup name={PARENT_CAMPAIGN}>
                <ParentCampaignNameField />
              </FormFieldGroup>
            ) : null
          }
          campaign={
            <MultiCampaigns
              name={CAMPAIGNS}
              depends={DEPENDENCIES[CAMPAIGNS]}
              platforms={platforms}
            />
          }
          label={<LabelsField />}
          owner={<OwnerField />}
          type={<TypeField />}
        />

        {isStatsDateAvailable && (
          <Grid container spacing={2}>
            <WarningStatus />
            <Grid item xs={4} md={4}>
              <FormField.DatePicker
                required
                name={TEST_CAMPAIGN_END_DATE}
                label={'Stats collection start date'}
                padding={0}
                minDate={dateFNS.addDays(new Date(Date.now()), 1)}
                placeholder={false}
              />
            </Grid>
          </Grid>
        )}

        {/* LINKS */}
        {linksConfig?.length ? (
          <CampaignsLinksSection
            buttons={
              <ButtonsField
                linksConfig={linksConfig}
                onChange={handleTypeChange}
              />
            }
            links={
              <LinkFields
                linkFormatType={linkType?.id}
                fields={visibleFields}
              />
            }
          />
        ) : null}

        {/* CP */}
        {isCPVisible ? <ConversionPageSection fields={visibleFields} /> : null}

        {/* REWARDS */}
        {isRewardVisible ? (
          <RewardsSection
            withReward={visibleFields.Reward}
            withAdditionalReward={visibleFields.AdditionalReward}
            withAdditionalRewardCondition={
              visibleFields.AdditionalRewardCondition
            }
            rewardPackage={rewardPackage}
          />
        ) : null}

        {/* CPP */}
        {isCPPVisible ? <CPPSection fields={visibleFields} /> : null}

        {/* PROMOCODE  */}
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <PromoCodeSection promoCode={<PromoCodeField />} />
          </Grid>
          <Grid item xs={4}>
            {linkFormatAvailable ? (
              <LinkFormatSection linkFormat={<LinkFormatField />} />
            ) : null}
          </Grid>
        </Grid>

        <PartnerCampaignIdSection
          fields={
            <Grid container spacing={3}>
              {campaigns?.map((_, i) => {
                const platformName =
                  campaigns.length > 1 ? platforms?.[i]?.name : '';

                return (
                  <Grid key={i} item xs={8}>
                    <CampaignPartnerInfoField
                      platformName={platformName}
                      campaignIndexField={i}
                    />
                  </Grid>
                );
              })}
            </Grid>
          }
        />

        <CostModelsSection
          costModels={
            <MultiCampaignsCostModels
              name={CAMPAIGNS}
              CostModelFormCreate={CostModelFormCreate}
              CostModelFormEdit={CostModelFormEdit}
              removableCostModels={removableCostModels}
              platforms={platforms}
            />
          }
        />
        {platforms?.length > 1 ? (
          <FormFieldGroup name={PARENT_CAMPAIGN}>
            <SpendAllocationModelSection campaigns={values[CAMPAIGNS]} />
          </FormFieldGroup>
        ) : null}
        <FraudProtectionArray disabledDeviceType={!platformId} />
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
      {childComponent}
    </SidePageV2>
  );
});

export default MultiCampaignsForm;
